<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import {SphButton} from '@/components/base/button';
import SphCheckbox from '@/components/base/checkbox/';
import SphCodeInput from '@/components/compound/code-input/CodeInput.vue';
import { SphField } from '@/components/base/field/';
import SphSpinner from '@/components/base/spinner/';

defineOptions({
    compatConfig: { MODE: 3 },
})
import { i18n } from '@legacy/js/plugin/vue-i18n';


export interface Props {
    actionPath: string;
    logoutPath: string;
    error?: string;
    authCodeParameterName: string;
    trustedParameterName: string;
    csrfToken: string;
    displayTrustedOption?: boolean;
    isClubity?: boolean;
    wasBackupCodeAttempt?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    error: '',
    displayTrustedOption: false,
    isClubity: false,
    wasBackupCodeAttempt: false,
});

const verifyWithBackupCode = ref(props.wasBackupCodeAttempt);

const codeDigits = computed(() => verifyWithBackupCode.value ? 8 : 6);
const codePattern = computed(() => verifyWithBackupCode.value ? '[a-zA-Z0-9]' : '[0-9]*');
const title = computed(() => verifyWithBackupCode.value ? i18n.t('Verifizierung mit Backup-Code') : i18n.t('6-stelligen Code aus der Authenticator-App eingeben.'));
const backupCodeButtonTitle = computed(() => verifyWithBackupCode.value ? i18n.t('Per Code einloggen') : i18n.t('Verifizierung mit Backup-Code'));
const verificationCode = ref('');
const verifying = ref(false);
const loginForm = ref<HTMLFormElement | null>(null);

const submitForm = async () => {
    if (!verificationCode.value) return;

    verifying.value = true;
    await nextTick();
    loginForm.value?.submit();
}

const logout = () => {
    document.location = props.logoutPath;
}

let logoutTimer: ReturnType<typeof setTimeout> | null = null;
onMounted(() => {
    logoutTimer = setTimeout(() => {
        logout();
    }, 180000);
});

onUnmounted(() => {
    if (logoutTimer) {
        clearTimeout(logoutTimer);
    }
});

</script>

<template>
    <div class="two-factor-auth">
        <p
            class="info-text mb-6"
        >
            <span class="block font-bold mb-1 text-lg">{{ $t('Zwei-Faktor-Authentifizierung') }}</span>
            {{ title }}
        </p>
        <form
            ref="loginForm"
            :action="actionPath"
            method="post"
        >
            <div
                v-if="displayTrustedOption"
                class="mb-6 block"
            >
                <SphField
                    id="trust-device"
                    v-slot="field"
                    :label="$t('Diesem Gerät für 30 Tage vertrauen')"
                    inline
                    compact
                >
                    <SphCheckbox
                        v-bind="field"
                        :name="trustedParameterName"
                    />
                </SphField>
            </div>

            <div
                v-if="error"
                class="kgr-inputGroup__text kgr-inputGroup__text--error mb-2"
            >
                {{ error }}
            </div>
            <div class="flex flex-col items-center md:items-start mb-8">
                <SphSpinner
                    v-if="verifying"
                    class="mx-auto"
                />

                <SphCodeInput
                    v-else
                    v-model:model-value="verificationCode"
                    :length="codeDigits"
                    :pattern="codePattern"
                    inputmode="decimal"
                    :disabled="verifying"
                    class="[&>input]:bg-white justify-center md:justify-start md:flex-nowrap"
                    @update:model-value="submitForm"
                />
            </div>

            <input
                :value="csrfToken"
                type="hidden"
                name="_csrf_token"
            />

            <div class="flex flex-col gap-4 items-center justify-center w-full md:flex-row md:justify-start">
                <SphButton
                    :label="backupCodeButtonTitle"
                    @click.prevent="() => {
                        verifyWithBackupCode = !verifyWithBackupCode;
                    }"
                />

                <SphButton
                    variant="ghost"
                    :label="$t('Abbrechen')"
                    @click.prevent="logout"
                />
            </div>


            <input
                :name="authCodeParameterName"
                :value="verificationCode"
                type="hidden"
            />
        </form>
    </div>
</template>

<style scoped lang="scss">
@reference "@/assets/styles/config.css";

.two-factor-auth {
    @apply flex flex-col items-center justify-center w-full;

    .info-text {
        @apply text-center;
    }

    form {
        @apply w-full;
    }
}
</style>
